import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronRight, Layers, MapPin, Plus } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button, EmptyState, Skeleton } from '../ui';
import { useMutation, useQuery } from '../gql/hooks';
import {
  CLIENTS_QUERY,
  CREATE_CLIENT_MUTATION,
  type ClientSummary,
  type ClientsQueryResult,
  type CreateClientMutationResult,
  type CreateClientMutationVariables,
} from '../gql/operations';
import type { CreateClientInput } from '../gql/types';
import { PageHeader } from '../features/shared/PageHeader';
import { ClientStatusBadge } from '../features/shared/StatusBadge';
import { ClientFormModal } from '../features/clients/ClientFormModal';
import { formatRelative } from '../lib/format';

// Clients (tenancy root, US-8.1.1). Lists every tenant as an editorial row card that drills
// into the client detail view. Loading -> skeletons; empty -> EmptyState; error -> banner.

function ClientRow({ client, delay }: { client: ClientSummary; delay: number }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const appCount = client.applications.length;

  return (
    <Link
      to={`/clients/${client.id}`}
      className={`group relative flex items-center gap-4 overflow-hidden rounded-2xl border p-5 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.05] to-white/[0.02] hover:border-white/[0.12]'
          : 'border-slate-200 bg-white shadow-sm hover:border-slate-300 hover:shadow-md'
      }`}
      style={{ animation: 'fadeInUp 0.6s ease-out forwards', animationDelay: `${delay}ms`, opacity: 0 }}
    >
      {/* Decorative blurred corner — depth on hover (§10). */}
      <div
        aria-hidden="true"
        className="absolute -bottom-8 -right-8 h-32 w-32 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 opacity-[0.07] blur-2xl transition-opacity duration-500 group-hover:opacity-20"
      />

      <span className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/20">
        <Building2 className="h-6 w-6 text-white" aria-hidden="true" />
      </span>

      <div className="relative min-w-0 flex-1">
        <div className="flex items-center gap-2.5">
          <h3
            className={`truncate text-lg font-semibold ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            {client.name}
          </h3>
          <ClientStatusBadge status={client.status} />
        </div>
        <div
          className={`mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${
            isDark ? 'text-white/40' : 'text-slate-500'
          }`}
        >
          <span className="inline-flex items-center gap-1.5">
            <Layers className="h-3.5 w-3.5" aria-hidden="true" />
            {appCount} {appCount === 1 ? 'application' : 'applications'}
          </span>
          {client.dataResidency && (
            <span className="inline-flex items-center gap-1.5 uppercase">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              {client.dataResidency}
            </span>
          )}
          <span>Updated {formatRelative(client.updatedAt)}</span>
        </div>
      </div>

      <ChevronRight
        className={`relative h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1 ${
          isDark ? 'text-white/20 group-hover:text-white/60' : 'text-slate-300 group-hover:text-slate-500'
        }`}
        aria-hidden="true"
      />
    </Link>
  );
}

export function ClientsPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { data, loading, error, refetch } = useQuery<ClientsQueryResult>(CLIENTS_QUERY);
  const create = useMutation<CreateClientMutationResult, CreateClientMutationVariables>(
    CREATE_CLIENT_MUTATION,
  );

  const [formOpen, setFormOpen] = useState(false);

  const clients = data?.clients ?? [];

  const openCreate = () => {
    create.reset();
    setFormOpen(true);
  };
  const closeCreate = () => {
    setFormOpen(false);
    create.reset();
  };

  const handleSubmit = async (input: CreateClientInput) => {
    try {
      await create.mutate({ input });
      setFormOpen(false);
      create.reset();
      refetch();
    } catch {
      // Error surfaced inline in the modal via create.error; keep the form open to retry.
    }
  };

  return (
    <div>
      <PageHeader
        title="Clients"
        description="Tenants on the gateway. Drill into a client to manage its applications and API keys."
        actions={
          <Button
            variant="primary"
            onClick={openCreate}
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            New client
          </Button>
        }
      />

      {error && (
        <div
          role="alert"
          className={`mb-6 flex items-center gap-3 rounded-xl p-4 ${
            isDark ? 'border border-rose-500/20 bg-rose-500/10' : 'border border-rose-200 bg-rose-50'
          }`}
        >
          <p className={`text-sm ${isDark ? 'text-rose-300' : 'text-rose-700'}`}>
            Couldn’t load clients: {error.message}
          </p>
        </div>
      )}

      {loading && (
        <div className="space-y-3" data-testid="clients-loading">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="block" height={84} />
          ))}
        </div>
      )}

      {!loading && !error && clients.length === 0 && (
        <EmptyState
          icon={<Building2 className="h-16 w-16" aria-hidden="true" />}
          title="No clients yet"
          description="Clients you onboard will appear here, each with their own applications and keys."
          action={{ label: 'Create first client', onClick: openCreate }}
        />
      )}

      {!loading && clients.length > 0 && (
        <ul className="space-y-3">
          {clients.map((client, i) => (
            <li key={client.id}>
              <ClientRow client={client} delay={(i + 1) * 80} />
            </li>
          ))}
        </ul>
      )}

      <ClientFormModal
        isOpen={formOpen}
        onClose={closeCreate}
        loading={create.loading}
        errorMessage={create.error?.message}
        onSubmit={(input) => void handleSubmit(input)}
      />
    </div>
  );
}
